import React from "react";
import { AbsoluteFill, Img, staticFile } from "remotion";
import {
  ShieldCheck,
  Zap,
  CheckCircle2,
  QrCode,
  Sparkles,
  ArrowRight,
  Clock,
} from "lucide-react";

export const KyuStoreFeedStill: React.FC = () => {
  // Brand Color Palette: Clean Esport Commercial (Deep Obsidian + Kyu Royal Purple + Electric Mint Accent)
  const bgDeep = "#07050E";
  const purpleLight = "#7952FF";
  const mintAccent = "#10B981";
  const goldAccent = "#F59E0B";

  return (
    <AbsoluteFill
      style={{
        width: 1080,
        height: 1080,
        backgroundColor: bgDeep,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', 'Segoe UI', Roboto, sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* 1. LAYER 1: Deep Volumetric Lighting & Texture Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 15% 15%, rgba(93, 63, 211, 0.35) 0%, transparent 45%),
            radial-gradient(circle at 85% 85%, rgba(16, 185, 129, 0.18) 0%, transparent 40%),
            radial-gradient(circle, rgba(255, 255, 255, 0.05) 1.5px, transparent 1.5px)
          `,
          backgroundSize: "100% 100%, 100% 100%, 36px 36px",
          pointerEvents: "none",
        }}
      />

      {/* Dynamic Sharp Angle Accents (Esport Geometric Cuts) */}
      <div
        style={{
          position: "absolute",
          top: -120,
          right: -120,
          width: 480,
          height: 480,
          border: "1px solid rgba(121, 82, 255, 0.18)",
          transform: "rotate(45deg)",
          background: "linear-gradient(135deg, rgba(93, 63, 211, 0.12) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          border: "1px solid rgba(16, 185, 129, 0.12)",
          transform: "rotate(45deg)",
          pointerEvents: "none",
        }}
      />

      {/* 2. LAYER 2: Main Layout Container (Full Bleed 1080x1080) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          padding: "54px 60px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxSizing: "border-box",
          zIndex: 2,
        }}
      >
        {/* HEADER: Brand Navigation & Status */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Brand Identity */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: 16,
                background: "linear-gradient(135deg, #5D3FD3 0%, #2A176B 100%)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 8px 24px rgba(93, 63, 211, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                padding: 6,
              }}
            >
              <Img
                src={staticFile("kyu_logo.png")}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                KYU STORE
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "2px 7px",
                    borderRadius: 6,
                    background: mintAccent,
                    color: "#000000",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  OFFICIAL
                </span>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255, 255, 255, 0.5)",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                }}
              >
                PROSES INSTAN 24 JAM
              </div>
            </div>
          </div>

          {/* Live Status Badge */}
          <div
            style={{
              padding: "10px 18px",
              borderRadius: 30,
              background: "rgba(255, 255, 255, 0.04)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(14px)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: mintAccent,
                boxShadow: `0 0 12px ${mintAccent}`,
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#E2E8F0",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              SERVER ONLINE
            </span>
          </div>
        </div>

        {/* HERO TITLE & HOOK */}
        <div style={{ marginTop: 24, marginBottom: 12 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              borderRadius: 8,
              background: "rgba(93, 63, 211, 0.15)",
              border: "1px solid rgba(121, 82, 255, 0.35)",
              marginBottom: 12,
            }}
          >
            <Zap size={14} color={purpleLight} />
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "1px",
                color: purpleLight,
                textTransform: "uppercase",
              }}
            >
              FLASH SALE HARI INI
            </span>
          </div>

          <div
            style={{
              fontSize: 54,
              fontWeight: 900,
              letterSpacing: "-0.04em",
              lineHeight: 1.05,
              textTransform: "uppercase",
            }}
          >
            TOP UP GAME INSTAN,
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #FFFFFF 0%, #10B981 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              HARGA RESELLER DETIKAN.
            </span>
          </div>
          <div
            style={{
              fontSize: 18,
              color: "rgba(255, 255, 255, 0.65)",
              fontWeight: 500,
              marginTop: 10,
              maxWidth: 720,
            }}
          >
            Tanpa ribet login password. Cukup masukkan User ID, bayar via QRIS otomatis, diamond langsung mendarat di akun game kamu.
          </div>
        </div>

        {/* PRODUCT CARDS SHOWCASE (GROUNDED IN REAL KYU STORE DATA) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.45fr 1fr 1fr",
            gap: 16,
            alignItems: "stretch",
          }}
        >
          {/* Card 1: STAR PRODUCT (Mobile Legends Weekly Diamond Pass) */}
          <div
            style={{
              background: "linear-gradient(145deg, rgba(93, 63, 211, 0.28) 0%, rgba(18, 12, 38, 0.85) 100%)",
              border: "1px solid rgba(121, 82, 255, 0.45)",
              borderRadius: 22,
              padding: "24px 22px",
              boxShadow: "0 20px 40px -10px rgba(93, 63, 211, 0.35)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Best Seller Pill */}
            <div
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                background: goldAccent,
                color: "#000000",
                fontSize: 10,
                fontWeight: 900,
                padding: "3px 8px",
                borderRadius: 6,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              ★ POPULER #1
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    overflow: "hidden",
                    border: "1.5px solid rgba(255, 255, 255, 0.2)",
                    flexShrink: 0,
                  }}
                >
                  <Img
                    src={staticFile("game_mlbb.jpg")}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.5)", fontWeight: 700, textTransform: "uppercase" }}>
                    Mobile Legends
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.2 }}>
                    Weekly Diamond Pass
                  </div>
                </div>
              </div>

              {/* Price Block */}
              <div style={{ margin: "16px 0 8px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.4)", textDecoration: "line-through", fontWeight: 600 }}>
                    Rp 35.000
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: mintAccent, background: "rgba(16, 185, 129, 0.15)", padding: "2px 6px", borderRadius: 4 }}>
                    HEMAT 28%
                  </span>
                </div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "#FFFFFF", letterSpacing: "-0.03em", marginTop: 2 }}>
                  Rp 26.550
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: mintAccent,
                fontWeight: 700,
                paddingTop: 12,
                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <CheckCircle2 size={14} />
              <span>Auto Claim 220 Diamond</span>
            </div>
          </div>

          {/* Card 2: Free Fire */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 22,
              padding: "22px 18px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    flexShrink: 0,
                  }}
                >
                  <Img
                    src={staticFile("game_ff.png")}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(255, 255, 255, 0.5)", fontWeight: 700, textTransform: "uppercase" }}>
                    Free Fire
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#FFFFFF" }}>
                    140 Diamond
                  </div>
                </div>
              </div>

              <div style={{ margin: "14px 0 6px 0" }}>
                <span style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.4)", textDecoration: "line-through", fontWeight: 600 }}>
                  Rp 22.000
                </span>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#FFFFFF", letterSpacing: "-0.02em" }}>
                  Rp 18.536
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                color: "#94A3B8",
                fontWeight: 600,
                paddingTop: 10,
                borderTop: "1px solid rgba(255, 255, 255, 0.06)",
              }}
            >
              <Zap size={12} color={goldAccent} />
              <span>Instan 5 Detik</span>
            </div>
          </div>

          {/* Card 3: Valorant Points */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 22,
              padding: "22px 18px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    flexShrink: 0,
                  }}
                >
                  <Img
                    src={staticFile("game_valo.jpg")}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(255, 255, 255, 0.5)", fontWeight: 700, textTransform: "uppercase" }}>
                    Valorant
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#FFFFFF" }}>
                    1.000 VP
                  </div>
                </div>
              </div>

              <div style={{ margin: "14px 0 6px 0" }}>
                <span style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.4)", textDecoration: "line-through", fontWeight: 600 }}>
                  Rp 135.000
                </span>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#FFFFFF", letterSpacing: "-0.02em" }}>
                  Rp 112.644
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                color: "#94A3B8",
                fontWeight: 600,
                paddingTop: 10,
                borderTop: "1px solid rgba(255, 255, 255, 0.06)",
              }}
            >
              <ShieldCheck size={12} color={mintAccent} />
              <span>100% Riot Resmi</span>
            </div>
          </div>
        </div>

        {/* 3 VALUE PILLARS (GROUNDED CONVERSION DRIVERS) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 14,
            margin: "14px 0",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderRadius: 14,
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(16, 185, 129, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckCircle2 size={18} color={mintAccent} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#FFFFFF" }}>
                Auto-Cek Nickname
              </div>
              <div style={{ fontSize: 11, color: "rgba(255, 255, 255, 0.5)" }}>
                Cegah salah kirim ID akun
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "14px 18px",
              borderRadius: 14,
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(93, 63, 211, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <QrCode size={18} color={purpleLight} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#FFFFFF" }}>
                QRIS Semua Bank
              </div>
              <div style={{ fontSize: 11, color: "rgba(255, 255, 255, 0.5)" }}>
                BCA, DANA, GoPay, OVO, Shopee
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "14px 18px",
              borderRadius: 14,
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(245, 158, 11, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Clock size={18} color={goldAccent} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#FFFFFF" }}>
                Invoice Otomatis
              </div>
              <div style={{ fontSize: 11, color: "rgba(255, 255, 255, 0.5)" }}>
                Bukti bayar instan di web & WA
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER CALL TO ACTION */}
        <div
          style={{
            background: "linear-gradient(90deg, #5D3FD3 0%, #10B981 100%)",
            borderRadius: 18,
            padding: "2px",
            boxShadow: "0 12px 30px rgba(93, 63, 211, 0.35)",
          }}
        >
          <div
            style={{
              background: "#0D0A1C",
              borderRadius: 16,
              padding: "16px 28px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: "rgba(16, 185, 129, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Sparkles size={20} color={mintAccent} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255, 255, 255, 0.7)", textTransform: "uppercase", letterSpacing: "1px" }}>
                  Kunjungi Website Resmi
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                  KYUSTORE.COM
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                color: "#FFFFFF",
                padding: "10px 22px",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.5px",
                boxShadow: "0 4px 15px rgba(16, 185, 129, 0.4)",
              }}
            >
              <span>BELI SEKARANG</span>
              <ArrowRight size={16} />
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
